import {
  MY_TASKS_GROUP_FILTER_TYPE,
  PrioritySortOrder,
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  STATUS_ORDER,
  STATUS_TYPE,
  STATUS_TYPE_LABELS,
  TASK_PRIORITY,
  TASK_PRIORITY_LABELS
} from '../global/constants';
import { updateTaskFilterGrouping, updateTaskFilterOrder } from '../helpers/common';
import {
  MobxFilteredCompletedTasksListInterface,
  MobxFilteredTasksListInterface,
  MobxMyTaskModelInterface
} from '../interfaces/TaskInterface';
import { ProjectModel } from '../mobx/models/project';
import { TaskModel } from '../mobx/models/task';
import { rootStore } from '../mobx/rootStore';
import { startOfWeek } from 'date-fns';
import { ProjectTemplateModel } from '../mobx/models/projectTemplate';
import { ViewModel } from '../mobx/models/view';
import { VIEWS_FILTER_TYPE } from '../mobx/interfaces/view';
import { defaultDisplayFilters } from '../mobx/common';
import { RawMilestone } from '../mobx/interfaces/project';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import 'dayjs/locale/en';

dayjs.extend(isBetween);

// /** ---------------------- PROJECT - TASK PAGE FILTER SERVICES ------------------------------ */

export const getTasksListByUser = (tasksList: TaskModel[], projectDetail?: ProjectModel) => {
  const { currentWorkspace } = rootStore.workspaceStore;
  const usersData = projectDetail
    ? new Set(projectDetail?.users?.map((item) => item?.id))
    : new Set(currentWorkspace?.users?.map((item) => item?.id));

  const { tasksGroupedByUser, users, tasksWithNoUsersTasks, tasksWithNoUsersCompletedTasks } = tasksList?.reduce(
    (acc, task) => {
      if (!task?.users?.length) {
        (task?.status ? acc.tasksWithNoUsersCompletedTasks : acc.tasksWithNoUsersTasks).push(task);
      } else {
        task.users.forEach((user) => {
          if (usersData.has(user?.id)) {
            acc.users[user.id] = user.name;
            (acc.tasksGroupedByUser[user.id] ||= []).push(task);
          }
        });
      }
      return acc;
    },
    {
      tasksGroupedByUser: {} as Record<string, TaskModel[]>,
      users: {} as Record<string, string>,
      tasksWithNoUsersTasks: [] as TaskModel[],
      tasksWithNoUsersCompletedTasks: [] as TaskModel[]
    }
  );

  const otherTasks: MobxFilteredCompletedTasksListInterface = {
    tasks: tasksWithNoUsersTasks,
    completedTasks: tasksWithNoUsersCompletedTasks
  };
  const filteredTasks: MobxFilteredTasksListInterface[] = Object.entries(tasksGroupedByUser).map(
    ([userId, userTasks]) => ({
      id: userId,
      name: users[userId],
      tasks: userTasks.filter((task) => !task?.status),
      completedTasks: userTasks.filter((task) => task?.status)
    })
  );

  return { filteredTasks: filteredTasks, otherTasks: otherTasks };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTasksListByStatus = (
  tasksList: TaskModel[],
  projectDetails: ProjectModel | ProjectTemplateModel,
  isShowRecentlyCompletedTask: boolean = false
) => {
  let otherTasks: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };

  const groupedTasks = new Map<string, TaskModel[]>();
  tasksList?.forEach((task) => {
    const statusId = task.statusId || 'other';
    if (!groupedTasks.has(statusId)) {
      groupedTasks.set(statusId, []);
    }
    groupedTasks.get(statusId)!.push(task);
  });

  const defaultTasks = groupedTasks.get('other') || [];

  const filteredTasks: MobxFilteredTasksListInterface[] = (projectDetails?.statusData || [])
    .map(({ _id: id, Name, Type }) => {
      const statusTasks = groupedTasks.get(id) || [];
      const additionalTasks = projectDetails?.defaultStatus
        ? projectDetails?.defaultStatus['_id'] === id
          ? defaultTasks
          : []
        : [];
      // Merge the tasks
      let allTasks = statusTasks.concat(additionalTasks);

      // Sort tasks if the status is COMPLETED (5)
      if (Type === STATUS_TYPE.COMPLETED && isShowRecentlyCompletedTask) {
        allTasks.sort((a, b) => new Date(b.updatedTime!).getTime() - new Date(a.updatedTime!).getTime());
      }

      const result: MobxFilteredTasksListInterface = {
        id: id,
        name: Name || '',
        tasks: allTasks,
        status: Type,
        completedTasks: []
      };

      return result;
    })
    .sort((a, b) => STATUS_ORDER.indexOf(a.status!) - STATUS_ORDER.indexOf(b.status!));

  return { filteredTasks, otherTasks };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTasksByPriority = (tasksList: TaskModel[]) => {
  const otherTasks: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };
  const filteredTasksMap = tasksList.reduce((map, task) => {
    if (!task.project?.priority.enabled) {
      task.status ? otherTasks.completedTasks?.push(task) : otherTasks.tasks.push(task);
      return map;
    }

    const priority = task.priority || TASK_PRIORITY.NO_PRIORITY;

    if (!map.has(priority)) {
      map.set(priority, {
        id: priority,
        name: TASK_PRIORITY_LABELS[priority],
        tasks: [],
        completedTasks: []
      });
    }

    const current = map.get(priority);
    task.status ? current?.completedTasks?.push(task) : current?.tasks?.push(task);

    return map;
  }, new Map<number, MobxFilteredTasksListInterface>());

  const filteredTasks: MobxFilteredTasksListInterface[] = Array.from(filteredTasksMap.values());
  filteredTasks.sort((a, b) => PrioritySortOrder.indexOf(a.id as number) - PrioritySortOrder.indexOf(b.id as number));
  return { filteredTasks, otherTasks };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTasksByProject = (tasksList: TaskModel[]) => {
  const otherTasks: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };
  const filteredTasksMap = tasksList.reduce((map, task) => {
    const projectId = task.projectId || 'no-project';
    const projectName = task.project?.name || 'No Project';

    if (!map.has(projectId)) {
      map.set(projectId, {
        id: projectId,
        name: projectName,
        tasks: [],
        completedTasks: []
      });
    }

    const currentGroup = map.get(projectId);
    if (task.status) {
      currentGroup?.completedTasks?.push(task);
    } else {
      currentGroup?.tasks?.push(task);
    }

    return map;
  }, new Map<string, MobxFilteredTasksListInterface>());

  const filteredTasks: MobxFilteredTasksListInterface[] = Array.from(filteredTasksMap.values());

  // Optional: Sort projects by name
  filteredTasks.sort((a, b) => a.name.localeCompare(b.name));

  return { filteredTasks, otherTasks };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTasksListByNoGrouping = (tasksList: TaskModel[], projectId: string) => {
  const otherTasks: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };

  const { tasks, completedTasks } = tasksList.reduce(
    (acc, task) => {
      if (task.status) {
        acc.completedTasks.push(task);
      } else {
        acc.tasks.push(task);
      }
      return acc;
    },
    { tasks: [] as TaskModel[], completedTasks: [] as TaskModel[] }
  );

  const filteredTasks: MobxFilteredTasksListInterface[] = [
    {
      id: 'No Grouping',
      index: 0,
      name: 'Other Tasks',
      projectId,
      tasks,
      completedTasks
    }
  ];

  return { filteredTasks: filteredTasks, otherTasks: otherTasks };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTaskListByGroup = (taskList: TaskModel[], isProjectTemplate?: boolean) => {
  const taskGroups = isProjectTemplate
    ? rootStore.projectTemplateStore.currentProjectTemplate?.taskGroups || []
    : rootStore.projectStore.currentProject?.taskGroups || [];

  const taskGroupIds = new Set(taskGroups.map((group) => group.id));
  const groupedTasks = taskList.reduce<Record<string, TaskModel[]>>((acc, item) => {
    // If item has groupId but that group doesn't exist in taskGroups, treat as 'other'
    const groupId = item.groupId && taskGroupIds.has(item.groupId) ? item.groupId : 'other';
    const key = item.status ? `${groupId}_completed` : groupId;

    if (!acc[key]) acc[key] = [];
    acc[key].push(item);

    return acc;
  }, {});

  const taskWithGroups: MobxFilteredTasksListInterface[] = taskGroups?.map((item) => ({
    ...item,
    tasks: groupedTasks[item?.id] || [],
    completedTasks: groupedTasks[`${item?.id}_completed`] || [],
    id: item?.id
  }));

  const otherTask: MobxFilteredCompletedTasksListInterface = {
    tasks: groupedTasks['other'] || [],
    completedTasks: groupedTasks['other_completed'] || []
  };
  return { filteredTasks: taskWithGroups, otherTasks: otherTask };
};

//----------------------------------------------------------------------------------------------------------------------

export const getTaskListByMilestone = (taskList: TaskModel[]) => {
  const milestoneList = rootStore.projectStore.currentProject?.milestones || [];
  // Create a milestone map for quick lookup
  const milestoneMap = milestoneList.reduce((acc: Record<string, string>, milestone: RawMilestone) => {
    acc[milestone.id] = milestone.milestoneName;
    return acc;
  }, {});

  // Initialize the milestoneWiseTask array and otherTasks array
  const milestoneWiseTask: MobxFilteredTasksListInterface[] = [];
  const otherTasks: TaskModel[] = [];

  // Group tasks by milestone using reduce
  const tasksGroupedByMilestone = taskList.reduce((acc: Record<string, TaskModel[]>, task) => {
    const milestoneId = task.milestoneId || 'Other';
    if (!acc[milestoneId]) {
      acc[milestoneId] = [];
    }
    acc[milestoneId].push(task);
    return acc;
  }, {});

  // Process grouped tasks
  Object.entries(tasksGroupedByMilestone).forEach(([milestoneId, tasks]) => {
    const milestoneName = milestoneMap[milestoneId] || 'Other';

    if (milestoneName === 'Other') {
      otherTasks.push(...tasks);
    } else {
      milestoneWiseTask.push({
        id: milestoneId,
        tasks: tasks.filter((task) => !task.status),
        completedTasks: tasks.filter((task) => task.status),
        name: milestoneName
      });
    }
  });

  // Add other tasks if any
  if (otherTasks.length > 0) {
    milestoneWiseTask.push({
      id: 'other',
      tasks: otherTasks.filter((task) => !task.status),
      completedTasks: otherTasks.filter((task) => task.status),
      name: 'Other'
    });
  }

  // Convert to array
  const tasksRef: MobxFilteredTasksListInterface[] = Object.values(milestoneWiseTask);
  const otherTask: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };

  return { filteredTasks: tasksRef, otherTasks: otherTask };
};

//----------------------------------------------------------------------------------------------------------------------

export const sortByDueDate = (tasks: TaskModel[]) => {
  return tasks.toSorted((a, b) => {
    const dateA = a.dueDate ? new Date(a.dueDate).getTime() : new Date().getTime();
    const dateB = b.dueDate ? new Date(b.dueDate).getTime() : new Date().getTime();
    return dateA - dateB;
  });
};

export const sortByCreatedDate = (tasks: TaskModel[]) => {
  return tasks.toSorted((a, b) => {
    const dateA = a.createdOn ? new Date(a.createdOn).getTime() : new Date().getTime();
    const dateB = b.createdOn ? new Date(b.createdOn).getTime() : new Date().getTime();
    return dateA - dateB;
  });
};

export const sortByPriority = (tasks: TaskModel[]) => {
  return tasks.sort(
    (a, b) =>
      PrioritySortOrder.indexOf(a.priority || TASK_PRIORITY.NO_PRIORITY) -
      PrioritySortOrder.indexOf(b.priority || TASK_PRIORITY.NO_PRIORITY)
  );
};

export const sortByIndex = (tasks: TaskModel[]) => {
  return tasks.toSorted((a, b) => a.index - b.index);
};

export const taskSorter = (
  type: number,
  tasks: TaskModel[],
  completedTask: TaskModel[],
  isShowRecentlyCompletedTask?: boolean
) => {
  let sortedTasks = tasks;
  let sortedCompletedTasks = completedTask;
  if (isShowRecentlyCompletedTask) {
    sortedTasks = sortedTasks.sort((a, b) => new Date(b.updatedTime!).getTime() - new Date(a.updatedTime!).getTime());
    sortedCompletedTasks = sortedCompletedTasks.sort(
      (a, b) => new Date(b.updatedTime!).getTime() - new Date(a.updatedTime!).getTime()
    );
    return { tasks: sortedTasks, completedTasks: sortedCompletedTasks };
  }
  switch (type) {
    case SHOW_TASK_BY_ORDER.DUE_DATE:
      sortedTasks = sortByDueDate(tasks);
      sortedCompletedTasks = sortByDueDate(completedTask);
      break;
    case SHOW_TASK_BY_ORDER.PRIORITY:
      sortedTasks = sortByPriority(tasks);
      sortedCompletedTasks = sortByPriority(completedTask);
      break;
    case SHOW_TASK_BY_ORDER.CUSTOM:
      sortedTasks = sortByIndex(tasks);
      sortedCompletedTasks = sortByIndex(completedTask);
      break;
    default:
      sortedTasks = sortByCreatedDate(tasks);
      sortedCompletedTasks = sortByCreatedDate(completedTask);
      break;
  }
  return { tasks: sortedTasks, completedTasks: sortedCompletedTasks };
};

export const applyTaskFilter = (tasks: TaskModel[], project: ProjectModel) => {
  const { showRecentlyCompletedTask } = project?.taskFilterType;
  const updatedGroupingStatus = updateTaskFilterGrouping(project);
  const updatedOrderByStatus = updateTaskFilterOrder(project);
  let result: { filteredTasks: MobxFilteredTasksListInterface[]; otherTasks: MobxFilteredCompletedTasksListInterface } =
    {
      filteredTasks: [],
      otherTasks: { tasks: [], completedTasks: [] }
    };
  // Apply grouping based on filter
  switch (updatedGroupingStatus) {
    case SHOW_TASK_GROUPING_TYPE.NO_GROUPING:
      result = getTasksListByNoGrouping(tasks, project.id);
      break;
    case SHOW_TASK_GROUPING_TYPE.DEFAULT:
      if (project.statusEnable) {
        result = getTasksListByStatus(tasks, project, showRecentlyCompletedTask);
      } else if (project.isGroupEnabled) {
        result = getTaskListByGroup(tasks, false);
      } else {
        result = getTasksListByNoGrouping(tasks, project.id);
      }
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY:
      result = getTasksByPriority(tasks);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER:
      result = getTasksListByUser(tasks, project);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_MILESTONE:
      result = getTaskListByMilestone(tasks);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS:
      result = getTasksListByStatus(tasks, project, showRecentlyCompletedTask);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_GROUP:
      result = getTaskListByGroup(tasks, false);
      break;
    default:
      result = getTaskListByGroup(tasks, false);
      break;
  }

  // Apply sorting to each group
  if (result.filteredTasks && result.filteredTasks.length > 0) {
    result.filteredTasks = result.filteredTasks.map((groupItem) => {
      if (groupItem.tasks && groupItem.completedTasks) {
        let sorted;
        if (
          updatedGroupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS &&
          groupItem.status === STATUS_TYPE.COMPLETED &&
          showRecentlyCompletedTask
        ) {
          sorted = taskSorter(updatedOrderByStatus, groupItem.tasks, groupItem.completedTasks, true);
        } else {
          sorted = taskSorter(updatedOrderByStatus, groupItem.tasks, groupItem.completedTasks, false);
        }
        return {
          ...groupItem,
          tasks: sorted.tasks,
          completedTasks: sorted.completedTasks
        };
      }
      return groupItem;
    });
  }

  // Sort other tasks
  if (result.otherTasks) {
    const sorted = taskSorter(
      updatedOrderByStatus,
      result.otherTasks.tasks,
      result.otherTasks.completedTasks || [],
      false
    );
    result.otherTasks = { tasks: sorted.tasks, completedTasks: sorted.completedTasks };
  }
  return result;
};

export const filterCompletedTasksByDate = (project: ProjectModel): TaskModel[] => {
  const { taskFilterType: taskFilter, tasks } = project;
  const groupingStatus = updateTaskFilterGrouping(project);
  if (groupingStatus !== SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS) {
    return tasks.filter((task) => !task.parentTaskId);
  }
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
  let endDate: Date | null = null;

  const cancelledStatus = project.statusData?.find((item) => item.Type === STATUS_TYPE.CANCELLED);

  switch (taskFilter.showCompletedTaskByFilter) {
    case SHOW_COMPLETED_TASK_BY_DATE.YESTERDAY:
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      endDate.setHours(0, 0, 0, 0);
      break;

    case SHOW_COMPLETED_TASK_BY_DATE.LAST_WEEK:
      endDate = new Date(today);
      endDate.setDate(today.getDate() - today.getDay() - 7);
      endDate.setHours(0, 0, 0, 0);
      break;

    case SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH:
      endDate = new Date(today.getFullYear(), today.getMonth() - 1, new Date().getDate());
      endDate.setHours(0, 0, 0, 0);
      break;

    case SHOW_COMPLETED_TASK_BY_DATE.NONE:
      return tasks.filter((task) => !task.parentTaskId && (!task.status || task.statusId === cancelledStatus?.['_id']));

    case SHOW_COMPLETED_TASK_BY_DATE.ALL:
    default:
      return tasks.filter((task) => !task.parentTaskId);
  }
  return tasks.filter((task) => {
    return (
      !task.parentTaskId &&
      (!task.status ||
        task.statusId === cancelledStatus?.['_id'] ||
        (endDate && task?.updatedTime && task?.updatedTime >= endDate))
    );
  });
};

//---------------------------------------- MY Task filter services -----------------------------------------------------

/** Group tasks by group name */
const groupTasksByGroupName = (tasks: TaskModel[]) => {
  const groupedTasks = tasks.map((task) => {
    const title = task?.project?.taskGroups?.find((group) => group?.id === task?.groupId)?.name || 'Other';
    return { task, title };
  });

  const groupedByGroupName = groupedTasks.reduce<{ title: string; tasks: TaskModel[] }[]>((acc, { task, title }) => {
    const existingGroup = acc.find((group) => group.title === title);
    if (existingGroup) {
      existingGroup.tasks.push(task);
    } else {
      acc.push({ title, tasks: [task] });
    }
    return acc;
  }, []);
  return groupedByGroupName;
};

/** Group tasks by priority */
const groupTasksByPriority = (tasks: TaskModel[]): { title: string; tasks: TaskModel[] }[] => {
  const priorityGroups = [
    TASK_PRIORITY.URGENT,
    TASK_PRIORITY.HIGH,
    TASK_PRIORITY.MEDIUM,
    TASK_PRIORITY.LOW,
    TASK_PRIORITY.NO_PRIORITY
  ];
  return priorityGroups.reduce<{ title: string; tasks: TaskModel[] }[]>((acc, priority) => {
    const filteredTasks = tasks.filter((task) => {
      if (priority === TASK_PRIORITY.NO_PRIORITY) {
        return task.priority === TASK_PRIORITY.NO_PRIORITY || task.priority === null;
      }
      return task.priority === priority;
    });
    if (filteredTasks.length > 0) {
      acc.push({ title: TASK_PRIORITY_LABELS[priority], tasks: filteredTasks });
    }
    return acc;
  }, []);
};

/** Group tasks by due date */
const groupTasksByDate = (tasks: TaskModel[]) => {
  const todayStart = dayjs().startOf('day');
  const todayEnd = dayjs().endOf('day');

  const grouped = tasks.reduce(
    (acc, task) => {
      if (!task.dueDate) {
        acc.Other.push(task);
      } else {
        const taskDueDate = dayjs(task.dueDate);
        if (taskDueDate.isBefore(todayStart)) {
          acc.Delayed.push(task);
        } else if (taskDueDate.isBetween(todayStart, todayEnd, null, '[]')) {
          acc.Today.push(task);
        } else {
          acc.Upcoming.push(task);
        }
      }
      return acc;
    },
    { Delayed: [], Today: [], Upcoming: [], Other: [] } as Record<string, TaskModel[]>
  );

  return Object.entries(grouped)
    .filter(([, tasks]) => tasks.length > 0)
    .map(([title, tasks]) => ({ title, tasks }));
};

const groupByProject = (tasks: TaskModel[]) => {
  const groupedMap = tasks.reduce((acc, task) => {
    if (!acc.has(task.projectId)) {
      acc.set(task.projectId, {
        project_id: task.projectId,
        tasks: [],
        title: task.project?.name || ''
      });
    }
    acc.get(task.projectId)!.tasks.push(task);
    return acc;
  }, new Map<string, { project_id: string; tasks: TaskModel[]; title: string }>());
  return Array.from(groupedMap.values());
};

/** Group tasks based on the filter type */
export const groupMyTasksByFilter = (tasks: TaskModel[], filterType: number): MobxMyTaskModelInterface[] => {
  // Sort tasks by due date (non-mutating copy so reading the grouping never reorders the source array)
  const sortedTasks = tasks.toSorted((a, b) => {
    // If either task has no due date, put it last
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;

    // Otherwise sort by due date
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });

  switch (filterType) {
    case MY_TASKS_GROUP_FILTER_TYPE.GROUP:
      return groupTasksByGroupName(sortedTasks);
    case MY_TASKS_GROUP_FILTER_TYPE.PROJECT:
      return groupByProject(sortedTasks);
    case MY_TASKS_GROUP_FILTER_TYPE.PRIORITY:
      return groupTasksByPriority(sortedTasks);
    case MY_TASKS_GROUP_FILTER_TYPE.NONE:
      return [{ title: '', tasks: sortedTasks }];
    default:
      return groupTasksByDate(sortedTasks);
  }
};

//---------------------------------------- calendar view services -----------------------------------------------------
export interface WeekDayTask {
  key: number;
  dayName: string;
  date: number;
  dateString: string;
  tasks: TaskModel[];
  completedTasks: TaskModel[];
}

export const getWeekTasks = (
  currentMonth: Date,
  weekTasks: TaskModel[],
  taskFilter?: {
    orderByStatus?: number;
    showRecentlyCompletedTask?: boolean;
  }
): WeekDayTask[] => {
  const startDate = startOfWeek(currentMonth, { weekStartsOn: 1 });
  const weekDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return weekDays.reduce<WeekDayTask[]>((days, dayName, i) => {
    const loopDate = dayjs(startDate).add(i, 'day').format('DD-MM-YYYY');
    const tasksForDay = weekTasks?.filter((x) => dayjs(x?.dueDate).format('DD-MM-YYYY') === loopDate);

    let thisDayTasks = tasksForDay?.filter((x) => !x?.status) || [];
    let completedTasks = tasksForDay?.filter((x) => x?.status) || [];

    if (taskFilter?.orderByStatus === SHOW_TASK_BY_ORDER.PRIORITY) {
      // PRIORITY
      const priorityGroups = [
        TASK_PRIORITY.URGENT,
        TASK_PRIORITY.HIGH,
        TASK_PRIORITY.MEDIUM,
        TASK_PRIORITY.LOW,
        TASK_PRIORITY.NO_PRIORITY
      ];
      const sortByPriority = (a: TaskModel, b: TaskModel) =>
        priorityGroups.indexOf(a?.priority || TASK_PRIORITY.NO_PRIORITY) -
        priorityGroups.indexOf(b?.priority || TASK_PRIORITY.NO_PRIORITY);

      thisDayTasks = thisDayTasks.toSorted(sortByPriority);
      completedTasks = completedTasks.toSorted(sortByPriority);
    } else if (taskFilter?.orderByStatus === 2) {
      // CREATED_DATE
      thisDayTasks = thisDayTasks.toSorted((a: TaskModel, b: TaskModel) => {
        const dateA = a?.dueDate ? new Date(a.dueDate) : new Date();
        const dateB = b?.dueDate ? new Date(b.dueDate) : new Date();
        return dateA.getTime() - dateB.getTime();
      });
      completedTasks = completedTasks.toSorted((a: TaskModel, b: TaskModel) => {
        const dateA = a?.dueDate || new Date();
        const dateB = b?.dueDate || new Date();
        return dateA.getTime() - dateB.getTime();
      });
    }

    if (taskFilter?.showRecentlyCompletedTask) {
      completedTasks = completedTasks.toSorted(
        (a, b) => new Date(b?.updatedTime || '').getTime() - new Date(a?.updatedTime || '').getTime()
      );
    }

    days.push({
      key: i + 1,
      dayName,
      date: Number(dayjs(startDate).add(i, 'day').format('DD')),
      dateString: dayjs(startDate).add(i, 'day').toISOString(),
      tasks: thisDayTasks,
      completedTasks
    });

    return days;
  }, []);
};

//---------------------------------------- template task filter services ------------------------------------
export const formatTemplateTasks = (tasks: TaskModel[], projectTemplate: ProjectTemplateModel) => {
  const { statusEnable, isGroupEnabled } = projectTemplate;
  let result;
  if (statusEnable) {
    result = getTasksListByStatus(tasks, projectTemplate, true);
  } else if (isGroupEnabled) {
    result = getTaskListByGroup(tasks, true);
  } else {
    result = getTasksListByNoGrouping(tasks, projectTemplate.id);
  }
  return result;
};

//---------------------------------------- View Tasks filter service ------------------------------------

const statusMap = [
  {
    type: STATUS_TYPE.BACKLOG,
    name: 'Backlog'
  },
  {
    type: STATUS_TYPE.UNSTARTED,
    name: 'Unstarted'
  },

  {
    type: STATUS_TYPE.IN_PROGRESS,
    name: 'In Progress'
  },
  {
    type: STATUS_TYPE.IN_REVIEW,
    name: 'In Review'
  },
  {
    type: STATUS_TYPE.COMPLETED,
    name: 'Completed'
  },
  {
    type: STATUS_TYPE.CANCELLED,
    name: 'Cancelled'
  }
];

export const filterCompletedViewTasksByDate = (tasks: TaskModel[], showCompletedTaskByFilter: number): TaskModel[] => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // Start of today
  let endDate: Date | null = null;

  switch (showCompletedTaskByFilter) {
    case SHOW_COMPLETED_TASK_BY_DATE.YESTERDAY:
      endDate = new Date(today);
      endDate.setDate(today.getDate() - 1);
      endDate.setHours(0, 0, 0, 0);
      break;

    case SHOW_COMPLETED_TASK_BY_DATE.LAST_WEEK:
      endDate = new Date(today);
      endDate.setDate(today.getDate() - today.getDay() - 7);
      endDate.setHours(0, 0, 0, 0);
      break;

    case SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH:
      endDate = new Date(today.getFullYear(), today.getMonth() - 1, new Date().getDate());
      endDate.setHours(0, 0, 0, 0);
      break;

    case SHOW_COMPLETED_TASK_BY_DATE.NONE:
      return tasks.filter((task) => !task.parentTaskId && !task.status);

    case SHOW_COMPLETED_TASK_BY_DATE.ALL:
    default:
      return tasks.filter((task) => !task.parentTaskId);
  }
  return tasks.filter((task) => {
    return !task.parentTaskId && (!task.status || (endDate && task?.updatedTime && task?.updatedTime >= endDate));
  });
};

const filterTasksByStatus = (tasks: TaskModel[], completedTaskFilter: number, filters: ViewModel['filters']) => {
  let otherTasks: MobxFilteredCompletedTasksListInterface = { tasks: [], completedTasks: [] };
  const groupedTasks = new Map<number, TaskModel[]>();
  tasks?.forEach((task) => {
    if (!task.statusDetails?.Type) {
      if (task.status) {
        otherTasks.completedTasks?.push(task);
      } else {
        otherTasks.tasks?.push(task);
      }
    } else {
      const statusType = task.statusDetails.Type;
      if (!groupedTasks.has(statusType)) {
        groupedTasks.set(statusType, []);
      }
      groupedTasks.get(statusType)!.push(task);
    }
  });

  const filteredTasks: MobxFilteredTasksListInterface[] = statusMap
    .map(({ type }) => {
      let statusTasks = groupedTasks.get(type) || [];

      // Sort tasks if the status is COMPLETED (5)
      if (type === STATUS_TYPE.COMPLETED && completedTaskFilter) {
        statusTasks = filterCompletedViewTasksByDate(statusTasks, completedTaskFilter);
      }

      const result: MobxFilteredTasksListInterface = {
        id: STATUS_TYPE_LABELS[type] || '',
        name: STATUS_TYPE_LABELS[type] || '',
        tasks: statusTasks,
        status: type,
        completedTasks: []
      };

      return result;
    })
    .sort((a, b) => STATUS_ORDER.indexOf(a.status!) - STATUS_ORDER.indexOf(b.status!));
  return { filteredTasks, otherTasks };
};

export const filterTasksByView = (
  tasks: TaskModel[],
  filters: ViewModel['filters'],
  viewFilter: ViewModel['displayFilters']
) => {
  let result;
  const {
    groupingStatus,
    orderByStatus,
    showRecentlyCompletedTask,
    showCompletedTaskByFilter = 0
  } = viewFilter || defaultDisplayFilters;

  switch (groupingStatus) {
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS:
      result = filterTasksByStatus(tasks, showCompletedTaskByFilter, filters);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PRIORITY:
      result = getTasksByPriority(tasks);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_USER:
      result = getTasksListByUser(tasks);
      break;
    case SHOW_TASK_GROUPING_TYPE.GROUPING_BY_PROJECT:
      result = getTasksByProject(tasks);
      break;
    default:
      result = getTasksListByNoGrouping(tasks, '');
  }

  // Apply sorting to each group
  if (result.filteredTasks && result.filteredTasks.length > 0) {
    result.filteredTasks = result.filteredTasks.map((groupItem) => {
      if (groupItem.tasks && groupItem.completedTasks) {
        let sorted;
        if (
          groupingStatus === SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS &&
          groupItem.status === STATUS_TYPE.COMPLETED &&
          showRecentlyCompletedTask
        ) {
          sorted = taskSorter(orderByStatus || 2, groupItem.tasks, groupItem.completedTasks, true);
        } else {
          sorted = taskSorter(orderByStatus || 2, groupItem.tasks, groupItem.completedTasks, false);
        }
        return {
          ...groupItem,
          tasks: sorted.tasks,
          completedTasks: sorted.completedTasks
        };
      }
      return groupItem;
    });
  }

  // Sort other tasks
  if (result.otherTasks) {
    const sorted = taskSorter(
      orderByStatus || 2,
      result.otherTasks.tasks,
      result.otherTasks.completedTasks || [],
      false
    );
    result.otherTasks = { tasks: sorted.tasks, completedTasks: sorted.completedTasks };
  }

  return result;
};

const getHiddenTasksCount = (viewTasks: TaskModel[], filteredTasks: MobxFilteredTasksListInterface[]) => {
  // Get parent and completed tasks in one pass
  const { parentTasks, completedTasks, completedSubTasks } = viewTasks.reduce<{
    parentTasks: TaskModel[];
    completedTasks: TaskModel[];
    completedSubTasks: TaskModel[];
  }>(
    (acc, task) => {
      if (!task.parentTaskId) {
        acc.parentTasks.push(task);
        if (task.status && task.statusId) {
          acc.completedTasks.push(task);
        }
      } else if (task.status && task.statusId) {
        acc.completedSubTasks.push(task);
      }
      return acc;
    },
    { parentTasks: [], completedTasks: [], completedSubTasks: [] }
  );

  // Get filtered tasks statuses
  const cancelledTasks =
    filteredTasks.find((item) => item.status === STATUS_TYPE.CANCELLED)?.tasks?.filter((item) => !item.parentTaskId) ||
    [];

  // Calculate final counts
  const completedTaskTotal = completedTasks.length + completedSubTasks.length - cancelledTasks.length;

  if (completedTaskTotal <= 0) return 0;

  const currentCompletedParentTasks =
    filteredTasks.find((item) => item.status === STATUS_TYPE.COMPLETED)?.tasks?.filter((item) => !item.parentTaskId) ||
    [];

  // Create lookup maps for faster checks
  const completedParentTaskIds = new Set(currentCompletedParentTasks.map((task) => task.id));
  const openParentTaskIds = new Set(parentTasks.filter((task) => !task.status).map((task) => task.id));
  const allParentTaskIds = new Set(parentTasks.map((task) => task.id));

  // Calculate sub-task counts
  const currentCompletedSubTasksCount = completedSubTasks.filter((task) =>
    completedParentTaskIds.has(task?.parentTaskId!)
  ).length;

  // Calculate subtask with open parent task count
  const uncompletedParentTaskCount = completedSubTasks.filter((task) =>
    openParentTaskIds.has(task?.parentTaskId!)
  ).length;

  // Calculate subtask with deleted parent task count
  const deletedParentTaskCount = completedSubTasks.filter((task) => !allParentTaskIds.has(task?.parentTaskId!)).length;

  const hiddenTaskCount =
    completedTaskTotal -
    currentCompletedParentTasks.length -
    currentCompletedSubTasksCount -
    uncompletedParentTaskCount -
    deletedParentTaskCount;

  return hiddenTaskCount;
};

export const filterTasks = (
  tasks: TaskModel[],
  filters: ViewModel['filters'],
  viewFilter: ViewModel['displayFilters']
) => {
  let viewTasks = tasks;

  // Simplified project/customer filtering logic
  if (
    (filters.project && filters.project.projects.length > 0) ||
    (filters.customer && filters.customer.customers.length > 0)
  ) {
    const hasProjectFilter = filters.project && filters.project.projects.length > 0;
    const hasCustomerFilter = filters.customer && filters.customer.customers.length > 0;

    if (hasProjectFilter && !hasCustomerFilter) {
      // Only project filter
      const isProjectIncluded = filters.project!.type === VIEWS_FILTER_TYPE.IS;
      const projectIds = filters.project!.projects;
      viewTasks = viewTasks.filter((task) =>
        isProjectIncluded ? projectIds.includes(task.projectId) : !projectIds.includes(task.projectId)
      );
    } else if (!hasProjectFilter && hasCustomerFilter) {
      // Only customer filter
      const isCustomerIncluded = filters.customer!.type === VIEWS_FILTER_TYPE.IS;
      const customerIds = filters.customer!.customers;
      viewTasks = viewTasks.filter((task) => {
        const taskCustomerId = task.project?.customerId;
        return isCustomerIncluded
          ? taskCustomerId && customerIds.includes(taskCustomerId)
          : !taskCustomerId || !customerIds.includes(taskCustomerId);
      });
    } else if (hasProjectFilter && hasCustomerFilter) {
      // Both project and customer filters - apply AND/OR logic based on filter types
      const projectType = filters.project!.type;
      const customerType = filters.customer!.type;
      const projectIds = filters.project!.projects;
      const customerIds = filters.customer!.customers;

      viewTasks = viewTasks.filter((task) => {
        const isProjectMatch = projectIds.includes(task.projectId);
        const isProjectExcluded = projectType === VIEWS_FILTER_TYPE.IS_NOT;
        const projectCondition = isProjectExcluded ? !isProjectMatch : isProjectMatch;

        const taskCustomerId = task.project?.customerId;
        const isCustomerMatch = taskCustomerId && customerIds.includes(taskCustomerId);
        const isCustomerExcluded = customerType === VIEWS_FILTER_TYPE.IS_NOT;
        const customerCondition = isCustomerExcluded ? !isCustomerMatch : isCustomerMatch;

        // OR logic for IS/IS, AND logic for mixed IS/IS_NOT
        if (projectType === VIEWS_FILTER_TYPE.IS && customerType === VIEWS_FILTER_TYPE.IS) {
          return projectCondition || customerCondition;
        } else {
          return projectCondition && customerCondition;
        }
      });
    }
  }

  if (filters.status && filters.status.statuses.length > 0) {
    if (filters.status.type === VIEWS_FILTER_TYPE.IS) {
      viewTasks = viewTasks.filter(
        (task) =>
          task.project?.statusEnable && task.statusDetails && filters.status?.statuses.includes(task.statusDetails.Type)
      );
    }

    if (filters.status.type === VIEWS_FILTER_TYPE.IS_NOT) {
      viewTasks = viewTasks.filter(
        (task) =>
          task.project?.statusEnable &&
          task.statusDetails &&
          !filters.status?.statuses.includes(task.statusDetails.Type)
      );
    }
  }

  if (filters.priority && filters.priority.priorities.length > 0) {
    if (filters.priority.type === VIEWS_FILTER_TYPE.IS) {
      viewTasks = viewTasks.filter(
        (task) =>
          task.project?.priority.enabled &&
          filters.priority?.priorities.includes(task.priority ?? task.project.priority.default)
      );
    }

    if (filters.priority.type === VIEWS_FILTER_TYPE.IS_NOT) {
      viewTasks = viewTasks.filter(
        (task) =>
          task.project?.priority.enabled &&
          !filters.priority?.priorities.includes(task.priority ?? task.project.priority.default)
      );
    }
  }

  if (filters.assign && filters.assign.assignees.length > 0) {
    const Unassigned = filters.assign.assignees.includes('unassigned');

    if (filters.assign.type === VIEWS_FILTER_TYPE.IS) {
      viewTasks = viewTasks.filter((task) => {
        const hasAssigneeMatch = task.usersIds?.some((assigneeId) => filters.assign?.assignees.includes(assigneeId));
        const isUnassigned = !task.users || task.users.length === 0;
        return (Unassigned && isUnassigned) || !!hasAssigneeMatch;
      });
    }

    if (filters.assign.type === VIEWS_FILTER_TYPE.IS_NOT) {
      viewTasks = viewTasks.filter((task) => {
        const hasAssigneeMatch = task.usersIds?.some((assigneeId) => filters.assign?.assignees.includes(assigneeId));
        const isUnassigned = !task.users || task.users.length === 0;
        const shouldExclude = !!hasAssigneeMatch || (Unassigned && isUnassigned);
        return !shouldExclude;
      });
    }
  }

  if (filters.dueDate && filters.dueDate.date.length > 0) {
    if (filters.dueDate.type === VIEWS_FILTER_TYPE.RANGE && filters.dueDate.date.length === 2) {
      const start = new Date(filters.dueDate.date[0]).setHours(0, 0, 0, 0);
      const end = new Date(filters.dueDate.date[1]).setHours(23, 59, 59, 999);
      viewTasks = viewTasks.filter((task) => {
        if (!task.dueDate) return false;
        const taskTime = new Date(task.dueDate).getTime();
        return taskTime >= start && taskTime <= end;
      });
    } else {
      const filteredDueDate = getFilteredDueDate(filters.dueDate.date[0]);
      if (!filteredDueDate) {
        if (filters.dueDate.type === VIEWS_FILTER_TYPE.IS) {
          viewTasks = viewTasks.filter((task) => !task.dueDate);
        }
        if (filters.dueDate.type === VIEWS_FILTER_TYPE.IS_NOT) {
          viewTasks = viewTasks.filter((task) => task.dueDate);
        }
      } else if (filters.dueDate.date[0] === 'In the past') {
        if (filters.dueDate.type === VIEWS_FILTER_TYPE.IS) {
          viewTasks = viewTasks.filter((task) => task.dueDate && task.dueDate < filteredDueDate);
        }
        if (filters.dueDate.type === VIEWS_FILTER_TYPE.IS_NOT) {
          viewTasks = viewTasks.filter((task) => !task.dueDate || task.dueDate > filteredDueDate);
        }
      } else {
        const label = filters.dueDate.date[0];
        const isFromNow =
          label === '1 day from now' ||
          label === '3 day from now' ||
          label === '1 week from now' ||
          label === '1 month from now' ||
          label === '3 months from now';

        if (isFromNow && filteredDueDate) {
          const start = new Date();
          start.setHours(0, 0, 0, 0);
          const end = new Date(filteredDueDate);
          end.setHours(23, 59, 59, 999);
          const startMs = start.getTime();
          const endMs = end.getTime();

          viewTasks = viewTasks.filter((task) => {
            if (!task.dueDate) return false;
            const taskTime = new Date(task.dueDate).getTime();
            return taskTime >= startMs && taskTime <= endMs;
          });
        } else {
          if (filters.dueDate.type === VIEWS_FILTER_TYPE.AFTER) {
            viewTasks = viewTasks.filter((task) => task.dueDate && task.dueDate > filteredDueDate);
          }
          if (filters.dueDate.type === VIEWS_FILTER_TYPE.BEFORE) {
            viewTasks = viewTasks.filter((task) => task.dueDate && task.dueDate < filteredDueDate);
          }
        }
      }
    }
  }

  const filteredTasks = filterTasksByView(viewTasks, filters, viewFilter) || {
    filteredTasks: [] as MobxFilteredTasksListInterface[],
    otherTasks: { tasks: [], completedTasks: [] }
  };

  const hiddenTasks = getHiddenTasksCount(viewTasks, filteredTasks.filteredTasks);

  return { ...(filteredTasks || {}), hiddenTasks: hiddenTasks || 0 };
};

export const getFilteredDueDate = (dueDate: string): Date | null => {
  const today = new Date();
  switch (dueDate) {
    case 'No due date':
      return null;
    case 'In the past':
      return new Date(today.getTime() - 24 * 60 * 60 * 1000); // Subtract 1 day in milliseconds
    case '1 day from now':
      return new Date(today.getTime() + 24 * 60 * 60 * 1000); // Add 1 day in milliseconds
    case '3 day from now':
      return new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000); // Add 3 days in milliseconds
    case '1 week from now':
      return new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000); // Add 7 days in milliseconds
    case '1 month from now':
      return new Date(today.getFullYear(), today.getMonth() + 1, today.getDate()); // Add 1 month
    case '3 months from now':
      return new Date(today.getFullYear(), today.getMonth() + 3, today.getDate()); // Add 3 months
    // case 'Custom date':
    //   return new Date(dueDate);
    default:
      return null;
  }
};
